// Lib
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { values as getValues } from 'lodash';

// Actions
import { register } from '../../authActions';

// Util
import {
    getUrlAbGroup,
    getUrlFeatureFlags,
    getUrlInitialEducation,
    getUrlInviteCode,
    getUrlMarketingReferralCode,
    getUrlPermissionId,
    getUrlPublicBoardId,
    getUrlRegisteredUserOnboarding,
    getUrlRegistrationAvenue,
    getUrlRegistrationEntryPoint,
    getUrlUserReferralCode,
    getUrlUtmParams,
} from '../../../app/routingSelector';
import { retrieveDeviceId } from '../../../device/deviceService';
import formSubmissionExceptionHandler from '../../../components/form/formSubmissionExceptionHandler';
import { shouldDisableSubmitButton } from '../../../components/form/finalForm/finalFormUtils';
import { getAmplitudeDeviceId } from '../../../analytics/amplitudeService';
import { getPlatformDetailsSelector } from '../../../platform/platformSelector';

// Components
import PasswordInput from '../../../components/form/fields/PasswordInput';
import StyledButton from '../../../components/buttons/StyledButton';
import FormInput from '../../../components/form/fields/FormInput';
import FieldValidationMessages from '../../../components/form/validation/FieldValidationMessages';
import AuthErrorMessage from '../../../components/form/messages/AuthErrorMessage';
import Spinner from '../../../components/loaders/Spinner';
import FinalForm from '../../../components/form/finalForm/FinalForm';
import FinalField from '../../../components/form/finalForm/FinalField';
import FormRow from '../../../components/form/FormRow';
import CheckboxInput from '../../../components/form/fields/CheckboxInput';

// Hooks
import useGetSocialOauthError from '../../useGetSocialOauthError';
import useGetInvitationEmail from './useGetInvitationEmail';

// Constants
import { REGISTER_FIELDS } from '../../../../common/validation/validationConstants';
import { isPlatformPhoneOrMobileMode, isPlatformTablet } from '../../../platform/utils/platformDetailsUtils';

const FORM_NAME = 'register';
const FORM_FIELDS = getValues(REGISTER_FIELDS);

const mapStateToProps = (state) => ({
    initialValues: {
        [REGISTER_FIELDS.agreeToTerms.name]: false,
        [REGISTER_FIELDS.subscribeToProductUpdates.name]: false,
        deviceId: retrieveDeviceId(),
        amplitudeDeviceId: getAmplitudeDeviceId(),
        inviteCode: getUrlInviteCode(state),
        referrerCode: getUrlUserReferralCode(state),
        marketingReferralCode: getUrlMarketingReferralCode(state),
        initialEducation: getUrlInitialEducation(state),
        onboarding: getUrlRegisteredUserOnboarding(state),
        registrationAvenue: getUrlRegistrationAvenue(state),
        registrationEntryPoint: getUrlRegistrationEntryPoint(state),
        publicBoardId: getUrlPublicBoardId(state),
        permissionId: getUrlPermissionId(state),
        featureFlags: getUrlFeatureFlags(state),
        guestAbGroup: getUrlAbGroup(state),
        utm: getUrlUtmParams(state),
    },
});

const mapDispatchToProps = (dispatch, { successCallback }) => ({
    dispatchRegister: (values) => dispatch(register({ ...values, successCallback })),
});

const enhance = connect(mapStateToProps, mapDispatchToProps);

const RegisterForm = (props) => {
    const { location, initialValues, dispatchRegister } = props;

    const givenNameInputRef = useRef();

    const oauthError = useGetSocialOauthError(location);
    const invitationEmail = useGetInvitationEmail(initialValues.inviteCode);

    const platformDetails = useSelector(getPlatformDetailsSelector);

    const enhancedInitialValues = useMemo(
        () => ({
            ...initialValues,
            email: invitationEmail,
            onboarding: initialValues.onboarding,
        }),
        [invitationEmail, initialValues, platformDetails],
    );

    const onSubmit = useCallback(async (values) => {
        try {
            await dispatchRegister(values);
        } catch (err) {
            return formSubmissionExceptionHandler({ form: FORM_NAME, sensitive: true })(err);
        }
    }, []);

    useEffect(() => {
        // Don't automatically focus the form on mobile or on tablet, as the keyboard hides too much
        // and various warning popups can interfere
        if (isPlatformPhoneOrMobileMode(platformDetails) || isPlatformTablet(platformDetails)) return;

        // Focuses the givenName input field when the page loads.
        givenNameInputRef.current && givenNameInputRef.current.focus();
    }, [initialValues.inviteCode, platformDetails]);

    return (
        <FinalForm className="auth-form" initialValues={enhancedInitialValues} onSubmit={onSubmit} fields={FORM_FIELDS}>
            {(formState) => (
                <>
                    <input
                        id="pwd"
                        className="fake-password"
                        tabIndex="-1"
                        aria-hidden="true"
                        type="password"
                        name="fakepasswordremembered"
                    />

                    <FormRow
                        label="Your name"
                        name={REGISTER_FIELDS.givenName.name}
                        errors={[formState.display.errors.givenName, formState.errors.familyName]}
                    >
                        <FinalField field={REGISTER_FIELDS.givenName}>
                            {({ input, display: fieldDisplay }) => (
                                <FormInput
                                    {...input}
                                    ref={givenNameInputRef}
                                    placeholder="First name"
                                    error={fieldDisplay.error}
                                    type={REGISTER_FIELDS.givenName.type}
                                    maxLength={REGISTER_FIELDS.givenName.maxLength}
                                />
                            )}
                        </FinalField>

                        <FinalField field={REGISTER_FIELDS.familyName}>
                            {({ input, display: fieldDisplay }) => (
                                <FormInput
                                    {...input}
                                    placeholder="Last name"
                                    error={fieldDisplay.error}
                                    type={REGISTER_FIELDS.familyName.type}
                                    maxLength={REGISTER_FIELDS.familyName.maxLength}
                                />
                            )}
                        </FinalField>
                    </FormRow>

                    <FinalField field={REGISTER_FIELDS.email}>
                        {({ input, display: fieldDisplay }) => (
                            <FormRow label="Email address" name={input.name} error={fieldDisplay.error}>
                                <FormInput
                                    {...input}
                                    type={REGISTER_FIELDS.email.type}
                                    maxLength={REGISTER_FIELDS.email.maxLength}
                                    error={fieldDisplay.error}
                                />
                            </FormRow>
                        )}
                    </FinalField>

                    <FinalField field={REGISTER_FIELDS.password}>
                        {({ input, display: fieldDisplay }) => (
                            <FormRow label="Choose a password" name={input.name} error={fieldDisplay.error}>
                                <PasswordInput
                                    {...input}
                                    maxLength={REGISTER_FIELDS.password.maxLength}
                                    canShowPassword
                                    error={fieldDisplay.error}
                                />
                            </FormRow>
                        )}
                    </FinalField>

                    <FinalField field={REGISTER_FIELDS.agreeToTerms}>
                        {({ input, display: fieldDisplay }) => (
                            <CheckboxInput
                                {...input}
                                className="auth-checkbox"
                                error={fieldDisplay.error}
                                label={
                                    <label htmlFor={input.name}>
                                        {/* eslint-disable-next-line max-len */}I agree to the Milanote{' '}
                                        <a
                                            href="https://milanote.com/legal/terms-of-service"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            terms
                                        </a>{' '}
                                        and{' '}
                                        <a
                                            href="https://milanote.com/legal/privacy-policy"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            privacy policy
                                        </a>
                                    </label>
                                }
                            />
                        )}
                    </FinalField>

                    <FinalField field={REGISTER_FIELDS.subscribeToProductUpdates}>
                        {({ input, display: fieldDisplay }) => (
                            <CheckboxInput
                                {...input}
                                className="auth-checkbox"
                                error={fieldDisplay.error}
                                label={
                                    <label htmlFor={input.name}>
                                        {/* eslint-disable-next-line max-len */}
                                        Send me occasional news & updates <span className="secondary">(optional)</span>
                                    </label>
                                }
                            />
                        )}
                    </FinalField>

                    <FinalField field={REGISTER_FIELDS.inviteCode}>
                        {({ display: fieldDisplay }) => <FieldValidationMessages sanitisedError={fieldDisplay.error} />}
                    </FinalField>

                    <StyledButton type="submit" className="row primary" disabled={shouldDisableSubmitButton(formState)}>
                        {!formState.submitting ? 'Register' : <Spinner show />}
                    </StyledButton>

                    <AuthErrorMessage
                        primaryError={formState.submitError || oauthError}
                        hide={formState.modifiedSinceLastSubmit || formState.submitting}
                    />
                </>
            )}
        </FinalForm>
    );
};

RegisterForm.propTypes = {
    location: PropTypes.object,
    initialValues: PropTypes.object,
    dispatchRegister: PropTypes.func,
};

export default enhance(RegisterForm);
